<template>
  <div>
    <div class="d-flex justify-content-between button-group">
      <div>
        <b-button v-if="!isNewContract" @click="openHistoryModal">{{$t('buttons.common.history')}}</b-button>
        <history-modal-content></history-modal-content>
        <lot-create-modal
          :contract="this.param"
          :isExchangeContract="this.isExchangeContract"
        ></lot-create-modal>
      </div>
      <div v-if="currentRoutePermissions.change_purchasecontract">
        <b-button class="btn" v-if="!isNewContract && isActiveContract" variant="warning" @click="createRequest"
                  size="sm">
          {{$t('buttons.requests.create_request')}}
        </b-button>
        <b-button class="btn" v-if="!isNewContract && isActiveContract" variant="success"
                  @click="completeContract" size="sm">
          {{$t('buttons.contracts.complete_contract')}}
        </b-button>
        <b-button class="btn" v-if="!isActiveContract" variant="success" @click="recoverContract" size="sm">
          {{$t('buttons.contracts.recover_contract')}}
        </b-button>
        <b-button v-if="isActiveContract" class="btn" variant="secondary" @click="save" size="sm">
          {{$t('buttons.common.save')}}
        </b-button>
      </div>
    </div>
    <div class="d-flex justify-content-between toggle-container" @click="showCollapse = !showCollapse"
         :aria-expanded="showCollapse ? 'true' : 'false'"
         aria-controls="collapse">
      <h3>
        {{contractLabel}}
        <span v-if="!isNewContract"
              :style="{color: isActiveContract ? 'green' : 'red'}"> - {{contractData.status}}</span>
      </h3>
      <div class="icons-wrapper">
        {{contractInfo}}
        <icon name="angle-down" class="angle" v-if="showCollapse"></icon>
        <icon name="angle-right" class="angle" v-else></icon>
      </div>
    </div>
    <b-collapse id="collapse" v-model="showCollapse">
      <div class="search-form">
        <div class="search-form__filters">
          <b-row>
            <b-col>
              <label>{{$t('labels.contracts.contract_number')}}</label>
              <input class="form-control" v-model="contractData.contract_nr"
                     placeholder="Enter contract No" :disabled="!isActiveContract"
                     :class="{ required: (!contractData.contract_nr) }">
            </b-col>
            <b-col>
              <label>{{$t('labels.common.seller')}}</label>
              <multiselect v-model="contractData.seller" :max-height="200"
                           placeholder="Choose seller" label="name" track-by="name"
                           :options="sellers" :multiple="false" :disabled="!isActiveContract"
                           :class="{ required: (!contractData.seller) }"
                           :close-on-select="true">
              </multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.measure_type')}}</label>
              <input class="form-control" value="m3" :disabled="true">
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.contracts.contract_date')}}</label>
              <datepicker v-model="contractData.date_start" lang="en" :disabled="!isActiveContract"
                          :class="{ required: (!contractData.date_start) }" :first-day-of-week="1"
              ></datepicker>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.buyer')}}</label>
              <multiselect v-model="contractData.buyer" :max-height="200"
                           placeholder="Choose buyer" label="name" track-by="id"
                           :options="customers" :disabled="!isActiveContract"
                           :class="{ required: (!contractData.buyer) }"
                           :taggable="true" :close-on-select="true"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.currency')}}</label>
              <multiselect v-model="contractData.currency" :max-height="200"
                           placeholder="Choose currency" label="code" track-by="id"
                           :options="currencies" :multiple="false" :disabled="!isActiveContract"
                           :class="{ required: (!contractData.currency) }"
                           :taggable="true" :close-on-select="true"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.contracts.contract_expiration_date')}}</label>
              <datepicker v-model="contractData.delivery_term" lang="en" placeholder="" :disabled="true"
                          :first-day-of-week="1"
              ></datepicker>
            </b-col>
            <b-col cols="4">
              <label>{{$t('labels.contracts.contract_type')}}</label>
              <multiselect v-model="contractData.type" :max-height="200"
                           placeholder="Choose contract type"
                           :options="contractTypes" :disabled="!isActiveContract"
                           :class="{ required: (!contractData.type) }"
                           :taggable="true" :close-on-select="true"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.notes')}}</label>
              <b-form-textarea v-model="contractData.notes"
                               placeholder="Enter notes"
                               :disabled="!isActiveContract">
              </b-form-textarea>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-collapse>
    <b-card no-body v-if="!isNewContract">
      <b-tabs card>
        <b-tab active>
          <template slot="title">
            <portal-target name="lots"></portal-target>
          </template>
          <div class="d-flex justify-content-between button-group">
            <portal to="lots">
              {{isExchangeContract ? $t('tabs.contracts.lots') : $t('tabs.contracts.specifications')}}
            </portal>
            <div>
              {{$t('labels.common.selected')}}: {{selectedRowsCount}}
              <b-button v-if="isActiveContract && currentRoutePermissions.change_purchasecontract" class="btn"
                        variant="danger" @click="deleteLot" size="sm">
                {{isExchangeContract ? $t('buttons.lots.delete_lot') : $t('buttons.lots.delete_spec')}}
              </b-button>
              <b-button v-if="isActiveContract && currentRoutePermissions.change_purchasecontract" class="btn"
                        @click="completeLot" variant="secondary"
                        size="sm">
                {{isExchangeContract ? $t('buttons.lots.complete_lot') : $t('buttons.lots.complete_spec')}}
              </b-button>
              <b-button v-if="isActiveContract && currentRoutePermissions.change_purchasecontract" class="btn"
                        @click="openProlongLotModal"
                        variant="secondary"
                        size="sm">
                {{isExchangeContract ? $t('buttons.lots.prolong_lot') : $t('buttons.lots.prolong_spec')}}
              </b-button>
              <b-modal ref="editLotModal"
                       size="sm"
                       ok-only
                       :title="prolongLotModalTitle"
                       ok-title="Save"
                       @ok="ProlongLot"
                       ok-variant="success"
                       :no-close-on-backdrop="true"
              >
                <b-row>
                  <b-col>
                    <label>{{$t('labels.lots.lots')}}</label>
                    <multiselect v-model="lotsProlongData.lots" :max-height="200"
                                 placeholder="Choose contract type"
                                 :options="lotsProlongData.lots" :multiple="true"
                                 :disabled="true"
                                 :taggable="true" :close-on-select="false"></multiselect>
                  </b-col>
                  <b-col>
                    <label>{{$t('labels.common.delivery_date')}}</label>
                    <datepicker v-model="lotsProlongData.date_delivery" lang="en"
                                :class="{ required: (!lotsProlongData.date_delivery) }"
                                :not-before="currentDate" :first-day-of-week="1"
                    ></datepicker>
                  </b-col>
                </b-row>
              </b-modal>
            </div>
            <div>
              <b-button v-if="isActiveContract && currentRoutePermissions.change_purchasecontract" class="btn"
                        variant="success" @click="openLotModal"
                        size="sm">
                {{isExchangeContract ? $t('buttons.lots.create_lot') : $t('buttons.lots.create_spec') }}
              </b-button>
            </div>
          </div>
          <ag-grid-vue class="ag-theme-balham" :gridOptions="gridOptions" :style="gridHeight"
                       :columnDefs="columns" :rowData="lots" :pinnedBottomRowData="pinnedRowData"
                       @rowDoubleClicked="openLotModal" @rowDataChanged="countSelectedRows"
                       @rowSelected="countSelectedRows" :postProcessPopup="postProcessPopup">
          </ag-grid-vue>
        </b-tab>
        <b-tab>
          <template slot="title">
            <portal-target name="purchaseOrders"></portal-target>
          </template>
          <portal to="purchaseOrders">
            <span>{{$t('tabs.contracts.purchaseOrders')}}</span>
            <b-badge variant="danger">
              {{unbindPackagesOrdersCount}}
            </b-badge>
          </portal>
          <div class="d-flex justify-content-between button-group">
            <div>
            </div>
            <div>
              <b-button class="btn" variant="outline-secondary" @click="openUndistPackagesModal"
                        size="sm">
                {{$t('buttons.contracts.undistributed_packages')}}
              </b-button>
            </div>
          </div>
          <ag-grid-vue class="ag-theme-balham grid"
                       :style="gridHeight"
                       :gridOptions="bindPckgsGridOptions"
                       :columnDefs="bindPackagesColumns"
                       :rowData="bindPackages"
                       :postProcessPopup="postProcessPopup">
          </ag-grid-vue>
          <b-modal ref="undistrPackagesModal"
                   :title="undistributedModalTitle"
                   :hide-footer="true" :no-close-on-backdrop="true">
            <ag-grid-vue class="ag-theme-balham grid"
                         :style="gridHeight"
                         :gridOptions="unbindPckgsGridOptions"
                         :columnDefs="unbindPackagesColumns"
                         :rowData="unbindPackages"
                         :postProcessPopup="postProcessPopup">
            </ag-grid-vue>
          </b-modal>
        </b-tab>
        <b-tab>
          <template slot="title">
            <portal-target name="docs"></portal-target>
          </template>
          <portal to="docs">
            {{$t('tabs.common.documents')}}
          </portal>
          <app-documents :documents="documents" :isCollapse="showCollapse" :url="commonUrl + 'attachments/'"
                         @loadDocuments="loadDocuments"></app-documents>
        </b-tab>
        <b-tab>
          <template slot="title">
            <portal-target name="comment"></portal-target>
          </template>
          <portal to="comment">
            {{$t('tabs.common.comments')}}
          </portal>
          <app-comments :comments="comments" :url="commonUrl + 'comments/'"
                        @loadComments="loadComments"></app-comments>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapGetters, mapState } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker';
  import Documents from '@/components/common/Documents';
  import Comments from '@/components/common/Comments';
  import RangeDatePicker from 'vue2-datepicker';
  import moment from 'moment';
  import LotCreateModal from '@/components/modals/PurchaseLotModal';
  import HistoryModalContent from '@/components/modals/HistoryModal';
  import {
    cleanFilters,
    getArrayByProperty,
    handlerResponse,
    setNotifyData
  } from '@/helpers/commonHelpers';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import { gridMixins } from '@/mixins';
  import { DATE_FORMAT } from '@/constants';

  export default {
    mixins: [gridMixins],
    components: {
      Multiselect,
      RangeDatePicker,
      'ag-grid-vue': AgGridVue,
      Datepicker,
      LotCreateModal,
      HistoryModalContent,
      'app-documents': Documents,
      'app-comments': Comments
    },
    data() {
      return {
        selectedRowsCount: 0,
        showCollapse: true,
        lotsProlongData: {
          lots: [],
          date_delivery: ''
        },
        contractData: {
          buyer: '',
          seller: '',
          contract_nr: '',
          created: '',
          created_by: '',
          date_start: '',
          note: '',
          currency: '',
          delivery_term: '',
          type: ''
        }
      }
    },
    methods: {
      createRequest() {
        if (this.gridOptions.api.getSelectedRows().length) {
          const id = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id');
          this.$http.post(`purchase/request_items/create_request_order/`, {id})
            .then(handlerResponse).then((response) => {
            this.$router.push({path: `/requests/${response}`});
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          })
        } else {
          this.$notify(setNotifyData('', 'Select at least one lot to create request order.', 'error'));
        }
      },
      openLotModal(params) {
        if (this.currentRoutePermissions.change_purchasecontract) {
          if (params.data) {
            this.$http.get(`purchase/lots/${params.data.id}/`).then(handlerResponse).then((response) => {
              this.$root.$emit('openLotModal', {...response, isNewLot: false});
            });
          } else {
            this.$root.$emit('openLotModal', {
              currency: this.contractData.currency.code,
              isNewLot: true
            })
          }
        }
      },
      save() {
        const saveData = {
          ...this.contractData,
          date_start: this.contractData.date_start ? moment(this.contractData.date_start).format(DATE_FORMAT) : '',
          seller: this.contractData.seller ? this.contractData.seller.id : '',
          buyer: this.contractData.buyer ? this.contractData.buyer.id : '',
          status: 'active',
          currency: this.contractData.currency ? this.contractData.currency.id : ''
        };
        if (this.isNewContract) {
          this.$http.post(`purchase/contracts/`, saveData)
            .then(handlerResponse).then((response) => {
            this.contractData = {...response};
            this.getContractInfo(response.id);
            this.$notify(setNotifyData('Success', 'Contract has been created!', 'success'));
            this.$router.push({path: `/contracts/${response.id}`});
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          })
        } else {
          this.$http.put(`purchase/contracts/${this.param}/`, saveData).then(handlerResponse).then(() => {
            this.$store.dispatch('lots/loadLots', {contract_id: this.param});
            this.$notify(setNotifyData('Success', 'Contract has been saved!', 'success'));
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          });
        }
      },
      completeContract() {
        this.$http.put(`purchase/contracts/${this.param}/`, {status: 'completed'})
          .then(handlerResponse).then(() => {
          this.$router.push({path: `/contracts/`});
          this.$notify(setNotifyData('Success', 'Contract has been completed!', 'success'));
        }).catch((error) => {
          this.$notify(setNotifyData('', 'Unable to complete the contract!', 'error'));
        });
      },
      recoverContract() {
        this.$http.put(`purchase/contracts/${this.param}/`, {status: 'active'})
          .then((response) => {
            this.getContractInfo(response.body.id);
            this.$notify(setNotifyData('Success', 'Contract has been recovered!', 'success'));
          }).catch((error) => {
          console.log(error);
          notificationsRenderer(error.body, 'error');
        });
      },
      deleteLot() {
        if (this.gridOptions.api.getSelectedRows().length) {
          const id = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id');
          this.$http.delete(`purchase/lots/`, {body: {id: id}}).then((response) => {
            this.$store.dispatch('lots/loadLots', {contract_id: this.param});
            this.getContractInfo(this.param);
            this.$notify(setNotifyData('Success', response.body, 'success'));
          }).catch((error) => {
            console.log(error);
            this.$notify(setNotifyData('Error', error.body, 'error'));
          });
        } else {
          this.$notify(setNotifyData('Error', 'Select lots for removing', 'error'));
        }
      },
      completeLot() {
        if (this.gridOptions.api.getSelectedRows().length) {
          const completeLotData = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id').map((e) => {
            return {
              'id': e,
              'status': 'completed'
            }
          });
          this.$http.put('purchase/lots/', completeLotData).then(() => {
            this.$store.dispatch('lots/loadLots', {contract_id: this.param});
            this.$notify(setNotifyData('Success', 'Lot information has been saved', 'success'));
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          });
        } else {
          this.$notify(setNotifyData('', 'Choose at least one lot!', 'error'));
        }
      },
      openProlongLotModal() {
        this.lotsProlongData.lots = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'lot_nr');
        if (this.lotsProlongData.lots.length) {
          this.lotsProlongData.date_delivery = '';
          this.$refs['editLotModal'].show();
        } else {
          this.$notify(setNotifyData('', 'Choose at least one lot for edit', 'error'));
        }
      },
      ProlongLot(event) {
        if (!this.lotsProlongData.date_delivery) {
          event.preventDefault();
          this.$notify(setNotifyData('', 'Enter required fields', 'error'));
        } else {
          const data = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id').map((e) => {
            return {
              status: 'active',
              id: e,
              delivery_term: moment(this.lotsProlongData.date_delivery).format(DATE_FORMAT)
            }
          })
          this.$http.put('purchase/lots/', data).then(() => {
            this.$store.dispatch('lots/loadLots', {contract_id: this.param});
            this.getContractInfo(this.param);
            this.$notify(setNotifyData('Success', 'Lot information has been saved', 'success'));
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          });
        }
      },
      loadDocuments(cacheFlag) {
        if (cacheFlag) {
          this.$store.dispatch('contracts/loadDocumentsList', {id: this.param});
        } else {
          this.$store.dispatch('contracts/loadDocumentsList', {id: this.param});
        }
      },
      loadComments(cacheFlag) {
        if (cacheFlag) {
          this.$store.dispatch('contracts/loadCommentsList', {id: this.param});
        } else {
          this.$store.dispatch('contracts/loadCommentsList', {id: this.param});
        }
      },
      openUndistPackagesModal() {
        this.$refs['undistrPackagesModal'].show();
      },
      getContractInfo(id) {
        this.$http.get(`purchase/contracts/${id}/`).then(handlerResponse).then((response) => {
          this.contractData = response;
          this.$store.dispatch('contracts/loadDistributedPackages', id);
          this.$store.dispatch('contracts/loadUndistributedPackages', id);
          this.$store.dispatch('lots/loadLots', {contract_id: id});
          this.$store.dispatch('location/setPathName', response.contract_nr);
        }).catch((error) => {
          this.$router.push({path: `/contracts/new`});
          console.log(error);
          this.$notify(setNotifyData('', 'Contract with such ID has been not found. You can create new one', 'error'));
        });
      },
      countSelectedRows() {
        this.selectedRowsCount = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id') ? getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id').length : 0
      },
      openHistoryModal() {
        this.historySettings.title = this.contractData.contract_nr;
        this.$root.$emit('openHistoryModal', this.historySettings);
      },
      setDefaults() {
        //set default Buyer - PRASLAS
        this.contractData.buyer = this.customers.find(e => e.id === 1458);
        //set default currency - EUR
        this.contractData.currency = this.currencies.find(e => e.id === 1);
      },
      clearData() {
        this.$store.cache.delete('contracts/loadDocumentsList', {id: this.param});
        this.$store.cache.delete('contracts/loadCommentsList', {id: this.param});
        if (this.comments.length) {
          this.$store.commit('contracts/setCommentsList', []);
        }
        if (this.documents.length) {
          this.$store.commit('contracts/setDocumentsList', []);
        }
        this.$store.commit('lots/setData', []);
      }
    },
    computed: {
      ...mapGetters('partners', ['sellers', 'customers']),
      ...mapState('generic', ['currencies', 'currentRoutePermissions']),
      ...mapGetters('contracts', {
        bindPckgsGridOptions: 'bindPckgsGridOptions',
        unbindPckgsGridOptions: 'unbindPckgsGridOptions'
      }),
      ...mapState('contracts', ['documents', 'comments', 'contractTypes', 'bindPackages', 'unbindPackages',
        'bindPackagesColumns', 'unbindPackagesColumns', 'unbindPackagesOrdersCount']),
      ...mapGetters('lots', {
        gridOptions: 'gridOptions'
      }),
      ...mapState('lots', ['pinnedRowData', 'lots', 'columns']),
      ...mapGetters('purchaseOrders', {
        orderGridOptions: 'gridOptions'
      }),
      param() {
        return this.$route.params.id;
      },
      isNewContract() {
        return this.param === 'new';
      },
      commonUrl() {
        return `purchase/contracts/${this.param}/`;
      },
      isActiveContract() {
        return (this.contractData.status === 'active' || this.isNewContract) ? true : false
      },
      isExchangeContract() {
        return !!(this.contractData.type === 'exchange')
      },
      contractLabel() {
        return this.isNewContract ? `${this.$i18n.t('common_content.new')} ${this.$i18n.t('common_content.contract')}` : `${this.$i18n.t('common_content.contract')} / ${this.contractData.contract_nr}`
      },
      contractInfo() {
        return this.isNewContract ? '' : `${this.contractData.created} / ${this.$i18n.t('common_content.creator')}: ${this.contractData.created_by}`
      },
      currentDate() {
        return new Date();
      },
      historySettings() {
        return {url: `purchase_contract/?id=${this.param}`, title: ``};
      },
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: 'calc(100vh - 470px)'} : {height: 'calc(100vh - 270px)'};
        } else {
          return this.showCollapse ? {height: '400px'} : {height: 'calc(100vh - 270px)'};
        }
      },
      undistributedModalTitle() {
        return `${this.$i18n.t('modal_dialogs.undistributed_packages')}`
      },
      prolongLotModalTitle() {
        return `${this.$i18n.t('modal_dialogs.prolong_lot')}`
      }
    },
    async beforeMount() {
      cleanFilters(this.contractData);
      await this.$store.dispatch('partners/getCustomers');
      await this.$store.dispatch('generic/getCurrencies');
      await this.$store.dispatch('partners/getSellers');
      if (!this.isNewContract) {
        await this.$store.dispatch('contracts/loadDocumentsList', {id: this.param});
        await this.$store.dispatch('contracts/loadCommentsList', {id: this.param});
      } else {
        this.setDefaults();
      }
    },
    mounted() {
      if (!this.isNewContract) {
        this.getContractInfo(this.param);
      }
      this.$root.$on('updatePurchaseContractInfo', (payLoad) => {
        this.getContractInfo(payLoad.id);
      })
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },
    beforeDestroy() {
      this.$root.$off('updatePurchaseContractInfo');
    }
  }
</script>

<style scoped>

  .grid {
    height: 450px;
  }

  .counter {
    color: red;
  }

</style>



// WEBPACK FOOTER //
// src/components/pages/modules/purchases/contracts/PurchaseContract.vue